// ═══════════════════════════════════════════════════════════════
// CAPACITY WEIGHTS PANEL — Settings → Scoring
// Path: src/components/settings/CapacityWeightsPanel.jsx
//
// Chapter-level sliders for the 6 confidence components that
// make up the Capacity tab's 0-100% score. Auto-rebalance to
// 100% as the user drags — they can't be wrong.
//
// Silent rebalance (when a component has no data chapter-wide) is
// separate from this override and happens at scoring time.
// ═══════════════════════════════════════════════════════════════

import React, { useState, useMemo, useEffect } from "react";
import {
  Target,
  RotateCcw,
  Save,
  Info,
} from "lucide-react";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";
import { useApp } from "../../context/AppContext";
import { Card } from "../ui/Card";
import { ActionButton } from "../ui/ActionButton";
import { InfoBubble } from "../ui/InfoBubble";
import { DEFAULT_CAPACITY_WEIGHTS } from "../../utils/capacityScoring";
import { capacityWeightsService } from "../../services/capacityWeightsService";

const COMPONENT_META = [
  {
    key: "giving_history",
    label: "Giving history depth",
    description: "Gift count, tenure, and recency of giving",
  },
  {
    key: "relationship_depth",
    label: "Relationship depth",
    description: "Contact logs in the last 12 months + filed contact reports",
  },
  {
    key: "director_assessment",
    label: "Director assessment",
    description: "What you've entered about capacity, inclination, and confidence",
  },
  {
    key: "external_wealth",
    label: "External wealth data",
    description: "DonorSearch / iWave / WealthEngine imports",
  },
  {
    key: "engagement_signal",
    label: "Engagement signal",
    description: "Engagement score, pathway stage, event attendance",
  },
  {
    key: "network_role",
    label: "Network & role",
    description: "Board service, committees, persona tags, org affiliation",
  },
];

export const CapacityWeightsPanel = ({ palette }) => {
  const { capacityWeights, setCapacityWeights, showMessage } = useApp();

  // Work in integer percentages for easier UI; normalize on save.
  const [draft, setDraft] = useState(() =>
    COMPONENT_META.reduce((acc, m) => {
      acc[m.key] = Math.round((capacityWeights?.[m.key] || 0) * 100);
      return acc;
    }, {})
  );
  const [saving, setSaving] = useState(false);

  // Sync draft when context changes (another session edited)
  useEffect(() => {
    setDraft(
      COMPONENT_META.reduce((acc, m) => {
        acc[m.key] = Math.round((capacityWeights?.[m.key] || 0) * 100);
        return acc;
      }, {})
    );
  }, [capacityWeights]);

  const total = useMemo(
    () => Object.values(draft).reduce((a, b) => a + b, 0),
    [draft]
  );

  // When one slider moves, redistribute the delta evenly across
  // the other components so the total stays at 100.
  const handleChange = (key, nextValue) => {
    const newValue = Math.max(0, Math.min(100, nextValue));
    const oldValue = draft[key];
    const delta = newValue - oldValue;
    if (delta === 0) return;
    const others = Object.keys(draft).filter((k) => k !== key);
    const othersTotal = others.reduce((sum, k) => sum + draft[k], 0);
    const next = { ...draft, [key]: newValue };
    if (othersTotal === 0) {
      // Can't redistribute — just assign to the first other slider
      if (others[0]) next[others[0]] = 100 - newValue;
    } else {
      // Proportional reduction on the others
      const remaining = 100 - newValue;
      others.forEach((k) => {
        next[k] = Math.max(
          0,
          Math.round((draft[k] / othersTotal) * remaining)
        );
      });
      // Fix rounding drift: put any residual on the largest "other"
      const sumCheck = others.reduce((s, k) => s + next[k], 0) + newValue;
      const drift = 100 - sumCheck;
      if (drift !== 0) {
        const target = others.reduce(
          (a, b) => (next[a] >= next[b] ? a : b),
          others[0]
        );
        next[target] = Math.max(0, next[target] + drift);
      }
    }
    setDraft(next);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const asFractions = {};
      Object.keys(draft).forEach((k) => {
        asFractions[k] = draft[k] / 100;
      });
      const saved = await capacityWeightsService.update(asFractions);
      setCapacityWeights(saved);
      showMessage("Capacity weights saved");
    } catch (err) {
      showMessage("Could not save weights", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setSaving(true);
    try {
      const reset = await capacityWeightsService.reset();
      setCapacityWeights(reset);
      showMessage("Reset to default weights");
    } catch (err) {
      showMessage("Could not reset", "error");
    } finally {
      setSaving(false);
    }
  };

  const isCustomized = COMPONENT_META.some(
    (m) =>
      Math.round((capacityWeights?.[m.key] || 0) * 100) !==
      Math.round((DEFAULT_CAPACITY_WEIGHTS[m.key] || 0) * 100)
  );

  return (
    <Card style={{ marginBottom: 16 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 6,
        }}
      >
        <Target size={ICON_SIZES.md} color={palette.accent1} />
        <div
          style={{
            fontSize: FONT_SIZES.base,
            fontWeight: 700,
            color: palette.text1,
            fontFamily: FONT_FAMILY,
          }}
        >
          Capacity Confidence Weights
        </div>
        <InfoBubble
          size={ICON_SIZES.sm}
          text="The six components that combine into every donor's 0–100% capacity confidence score. These weights apply chapter-wide. Adjust them to reflect your fundraising philosophy — some shops prize giving history, others prize relationship depth."
        />
        {isCustomized && (
          <span
            style={{
              marginLeft: 4,
              fontSize: 10,
              fontWeight: 700,
              color: palette.accent1,
              background: `${palette.accent1}15`,
              padding: "3px 8px",
              borderRadius: 99,
              letterSpacing: 0.3,
            }}
          >
            CUSTOMIZED
          </span>
        )}
      </div>
      <p
        style={{
          color: palette.text2,
          fontSize: FONT_SIZES.sm,
          margin: "0 0 12px 0",
          lineHeight: 1.5,
        }}
      >
        Tune how the six signals combine into each donor's confidence
        score. Sliders auto-rebalance to 100%.
      </p>

      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 8,
          padding: 10,
          borderRadius: 6,
          background: `${palette.accent1}08`,
          border: `1px dashed ${palette.cardBorder}`,
          fontSize: 11,
          color: palette.text2,
          lineHeight: 1.5,
          marginBottom: 16,
        }}
      >
        <Info size={ICON_SIZES.sm} style={{ marginTop: 2, flexShrink: 0, color: palette.accent1 }} />
        <span>
          Components with no data for your chapter (e.g. external wealth
          until you import a DonorSearch CSV) are redistributed automatically
          at scoring time, so no donor is unfairly penalized.
        </span>
      </div>

      {COMPONENT_META.map((m) => {
        const pct = draft[m.key] || 0;
        return (
          <div key={m.key} style={{ marginBottom: 16 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                marginBottom: 4,
              }}
            >
              <div>
                <span
                  style={{
                    fontSize: FONT_SIZES.sm,
                    fontWeight: 700,
                    color: palette.text1,
                  }}
                >
                  {m.label}
                </span>
                <InfoBubble size={ICON_SIZES.xs} text={m.description} />
                <div
                  style={{
                    fontSize: 11,
                    color: palette.text3,
                    marginTop: 2,
                  }}
                >
                  {m.description}
                </div>
              </div>
              <span
                style={{
                  fontSize: 18,
                  fontWeight: 800,
                  color: palette.accent1,
                  fontFamily: FONT_FAMILY,
                }}
              >
                {pct}%
              </span>
            </div>
            <input
              type="range"
              min="0"
              max="100"
              value={pct}
              onChange={(e) => handleChange(m.key, parseInt(e.target.value, 10))}
              style={{ width: "100%", accentColor: palette.accent1 }}
            />
          </div>
        );
      })}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginTop: 12,
          paddingTop: 12,
          borderTop: `1px solid ${palette.cardBorder}`,
        }}
      >
        <div
          style={{
            fontSize: 12,
            color: total === 100 ? palette.text2 : "#dc2626",
            fontWeight: 600,
          }}
        >
          Total: <strong>{total}%</strong>
          {total !== 100 && " — will normalize on save"}
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <ActionButton
            label="Reset to Defaults"
            icon={RotateCcw}
            variant="secondary"
            onClick={handleReset}
            disabled={saving}
          />
          <ActionButton
            label={saving ? "Saving..." : "Save Weights"}
            icon={Save}
            onClick={handleSave}
            disabled={saving}
          />
        </div>
      </div>
    </Card>
  );
};
